import { useSearchParams } from "react-router-dom";
import { useMemo, useEffect } from "react";

export default function FilterRedesign({ cars, closeFilter }) {
  const [params, setParams] = useSearchParams();

  // Фиксиран ред на моделите
  const modelOrder = [
    "Capri",
    "E-Explorer",
    "Puma Gen-E",
    "Mach-E",
    "Kuga",
    "New Kuga",
    "Puma",
    "Mustang",
    "Ranger",
    "Bronco",
    "Focus",
    "Explorer",
    "Courier",
    "Tourneo Connect",
    "New Custom",
    "Transit",
  ];

  const filteredCars = useMemo(() => {
    const models = params.getAll("model").map((m) => m.toLowerCase());
    const fuels = params.getAll("fuel").map((f) => f.toLowerCase());
    const gearboxes = params.getAll("gearbox").map((g) => g.toLowerCase());
    const vats = params.getAll("vat").map((v) => v.toLowerCase());

    return cars.filter((car) => {
      const matchModel =
        models.length === 0 || models.includes(car.model.toLowerCase());
      const matchFuel =
        fuels.length === 0 || fuels.includes(car.fuel.toLowerCase());
      const matchGearbox =
        gearboxes.length === 0 || gearboxes.includes(car.gearbox.toLowerCase());
      const matchVat =
        vats.length === 0 || vats.includes(car.vat.toLowerCase());

      return matchModel && matchFuel && matchGearbox && matchVat;
    });
  }, [cars, params]);

  const availableModels = useMemo(() => {
    const modelCounts = {};
    filteredCars.forEach((car) => {
      const modelUpper = car.model.toUpperCase();
      modelCounts[modelUpper] = (modelCounts[modelUpper] || 0) + 1;
    });

    return modelOrder
      .map((model) => ({
        name: model,
        count: modelCounts[model.toUpperCase()] || 0,
      }))
      .filter((item) => item.count > 0);
  }, [filteredCars]);

  const fuels = useMemo(
    () => [...new Set(filteredCars.map((car) => car.fuel))],
    [filteredCars]
  );
  const gearboxes = useMemo(
    () => [...new Set(filteredCars.map((car) => car.gearbox))],
    [filteredCars]
  );
  const vats = useMemo(
    () => [...new Set(filteredCars.map((car) => car.vat))],
    [filteredCars]
  );

  const modelsList = params.getAll("model");
  const fuelsList = params.getAll("fuel");
  const gearboxesList = params.getAll("gearbox");
  const vatsList = params.getAll("vat");

  // Обновяване на състоянието на checkbox-овете при промяна на параметрите
  useEffect(() => {
    document.querySelectorAll("input").forEach((item) => {
      item.checked =
        modelsList.includes(item.value) ||
        fuelsList.includes(item.value) ||
        gearboxesList.includes(item.value) ||
        vatsList.includes(item.value);
    });
  }, [modelsList, fuelsList, gearboxesList, vatsList]);

  // Универсална функция за обработка на промените във филтрите
  function handleFilterChange(e, filterType) {
    const chosenValue = e.target.value;
    const filterList = params.getAll(filterType);
    if (filterList.includes(chosenValue)) {
      const updatedList = filterList.filter((item) => item !== chosenValue);
      params.delete(filterType);
      updatedList.forEach((item) => params.append(filterType, item));
    } else {
      params.append(filterType, chosenValue);
    }
    setParams(params);
  }

  // Функция за изчистване на всички филтри
  function clearFilter() {
    ["model", "fuel", "gearbox", "vat"].forEach((filterType) => {
      params.delete(filterType);
    });
    setParams(params);
  }

  return (
    <div className="sticky top-0 z-10 p-4 text-sm bg-white font-fordAntennaRegular">
      <h2 className="text-2xl">Филтър</h2>

      <details className="p-2 pb-4 mt-8 border-b">
        <summary className="uppercase cursor-pointer">
          &nbsp;&nbsp;Модел
        </summary>
        <div className="accordion-content">
          {availableModels.map(({ name, count }) => (
            <label
              key={name}
              htmlFor={name}
              className="flex items-center px-3 py-3 mt-2 bg-gray-100 cursor-pointer rounded-2xl"
            >
              <input
                type="checkbox"
                id={name}
                className="w-5 h-5 focus:outline-none focus:ring-0 text-fordBlue"
                onChange={(e) => handleFilterChange(e, "model")}
                value={name}
                hidden
              />
              <span className="pl-2 uppercase">
                {name} <span className="text-gray-400">({count})</span>
              </span>
            </label>
          ))}
        </div>
      </details>

      <details className="p-2 pb-4 mt-2 border-b">
        <summary className="uppercase cursor-pointer">
          &nbsp;&nbsp;Гориво
        </summary>
        <div className="accordion-content">
          {fuels.map((fuel) => (
            <label
              key={fuel}
              htmlFor={fuel}
              className="flex items-center px-3 py-3 mt-2 bg-gray-100 cursor-pointer rounded-2xl"
            >
              <input
                type="checkbox"
                className="w-5 h-5 focus:outline-none focus:ring-0 text-fordBlue"
                id={fuel}
                onChange={(e) => handleFilterChange(e, "fuel")}
                value={fuel}
                hidden
              />
              <span className="pl-2 capitalize">{fuel}</span>
            </label>
          ))}
        </div>
      </details>

      <details className="p-2 pb-4 mt-2 border-b">
        <summary className="uppercase cursor-pointer">
          &nbsp;&nbsp;Скоростна кутия
        </summary>
        <div className="accordion-content">
          {gearboxes.map((gearbox) => (
            <label
              key={gearbox}
              htmlFor={gearbox}
              className="flex items-center px-3 py-3 mt-2 bg-gray-100 cursor-pointer rounded-2xl"
            >
              <input
                type="checkbox"
                id={gearbox}
                className="w-5 h-5 focus:outline-none focus:ring-0 text-fordBlue"
                onChange={(e) => handleFilterChange(e, "gearbox")}
                value={gearbox}
                hidden
              />
              <span className="pl-2 capitalize">{gearbox}</span>
            </label>
          ))}
        </div>
      </details>

      <details className="p-2 pb-4 mt-2 border-b">
        <summary className="uppercase cursor-pointer">
          &nbsp;&nbsp;Право на данъчен кредит
        </summary>
        <div className="accordion-content">
          {vats.map((vat) => (
            <label
              key={vat}
              htmlFor={vat}
              className="flex items-center px-3 py-3 mt-2 bg-gray-100 cursor-pointer rounded-2xl"
            >
              <input
                type="checkbox"
                id={vat}
                className="w-5 h-5 focus:outline-none focus:ring-0 text-fordBlue"
                onChange={(e) => handleFilterChange(e, "vat")}
                value={vat}
                hidden
              />
              <span className="pl-2 capitalize">{vat}</span>
            </label>
          ))}
        </div>
      </details>

      <button className="mt-8 btn-primary lg:hidden" onClick={closeFilter}>
        Приложи филтрите
      </button>
      <button
        className="mt-4 btn-secondary border-none text-white bg-[#1700f4]"
        onClick={clearFilter}
      >
        Изчисти филтрите
      </button>
    </div>
  );
}
